/**
 * Bidirectional text helpers (Faz 8.5, OCS text_support.rs RTL parity).
 *
 * The model is pragmatic — NOT the full Unicode BiDi algorithm:
 * - Paragraph direction stays forced LTR (OCS behavior: no mirroring,
 *   no bracket flipping).
 * - After wrapping, runs of consecutive RTL words are reversed so a
 *   mixed Turkish-Arabic line reads with the RTL words in visual order.
 * - Within a mixed word, RTL segments are reversed and every RTL run is
 *   terminated with LRM (\u200E) to lock the direction for the shaper.
 *
 * The input stays in LOGICAL order everywhere (entity data, export) —
 * reordering happens only at render time, so no LRM ever leaks into the
 * stored text or the DXF output (Faz 8 risk note).
 */

/** LRM — left-to-right mark (invisible direction lock). */
export const LRM = '\u200E';

/** Bidi class R / AL / RTL-control Unicode ranges (OCS bidi_class set:
 *  R, AL, RLE, RLO, RLI). */
const RTL_RANGES: ReadonlyArray<readonly [number, number]> = [
    [0x0590, 0x05FF], // Hebrew
    [0x0600, 0x06FF], // Arabic
    [0x0700, 0x074F], // Syriac
    [0x0750, 0x077F], // Arabic supplement
    [0x0780, 0x07BF], // Thaana
    [0x07C0, 0x07FF], // N'Ko
    [0x0800, 0x083F], // Samaritan
    [0x0840, 0x085F], // Mandaic
    [0x08A0, 0x08FF], // Arabic extended-A
    [0xFB1D, 0xFB4F], // Hebrew presentation
    [0xFB50, 0xFDFF], // Arabic presentation-A
    [0xFE70, 0xFEFF], // Arabic presentation-B
    [0x202A, 0x202E], // LRE/LRO/RLE/RLO/PDF controls
    [0x2066, 0x2069], // LRI/RLI/FSI/PDI isolates
    [0x1E900, 0x1E94F], // Adlam
];

/** True when the code point carries bidi class R or AL (or an RTL control). */
export function isRtlChar(code: number): boolean {
    return RTL_RANGES.some(([lo, hi]) => code >= lo && code <= hi);
}

/** True when the text contains any RTL character. */
export function hasRtl(text: string): boolean {
    for (const ch of text) {
        if (isRtlChar(ch.codePointAt(0) ?? 0)) return true;
    }
    return false;
}

/** Direction of the first strong directional character ("ltr" | "rtl").
 *  Used by the in-place editor to mirror its input box. */
export function firstStrongDirection(text: string): 'ltr' | 'rtl' {
    for (const ch of text) {
        const code = ch.codePointAt(0) ?? 0;
        if (isRtlChar(code)) return 'rtl';
        // Strong LTR classes (approximated by basic Latin/Cyrillic/Greek).
        if ((code >= 0x0041 && code <= 0x005A) || (code >= 0x0061 && code <= 0x007A)
            || (code >= 0x0370 && code <= 0x03FF) || (code >= 0x0400 && code <= 0x04FF)) return 'ltr';
    }
    return 'ltr';
}

/** True when a word (whitespace-delimited) contains RTL characters. */
function isRtlWord(word: string): boolean {
    return hasRtl(word);
}

/**
 * OCS rtl_display_word: inside a MIXED word, reverse each RTL segment and
 * terminate every RTL run with LRM (pure-LTR words pass through untouched;
 * pure-RTL words are kept intact — their position is what gets reversed).
 */
function rtlDisplayWord(word: string): string {
    if (!hasRtl(word)) return word;
    const chars = Array.from(word);
    const out: string[] = [];
    let i = 0;
    while (i < chars.length) {
        if (isRtlChar(chars[i].codePointAt(0) ?? 0)) {
            let j = i;
            while (j < chars.length && isRtlChar(chars[j].codePointAt(0) ?? 0)) j++;
            // Neutral characters glued to the RTL run stay inside it.
            while (j < chars.length && !isRtlChar(chars[j].codePointAt(0) ?? 0)
                && !/[A-Za-z0-9]/.test(chars[j])) j++;
            out.push(...chars.slice(i, j).reverse(), LRM);
            i = j;
        } else {
            out.push(chars[i]);
            i++;
        }
    }
    return out.join('');
}

/**
 * Reorder one display line: paragraph direction stays LTR; runs of
 * consecutive RTL words reverse their ORDER (word-level UBA subset), and
 * each mixed word is internally reordered by rtlDisplayWord.
 */
export function reorderRtlLine(line: string): string {
    if (!hasRtl(line)) return line;
    // Keep the whitespace structure: split into word/non-word chunks.
    const tokens = line.split(/(\s+)/);
    // Find word token indices (non-whitespace).
    const wordIdx = tokens.map((t) => (t.length > 0 && !/^\s+$/.test(t) ? true : false));
    // Reverse runs of consecutive RTL words in place.
    const reordered = [...tokens];
    let i = 0;
    while (i < tokens.length) {
        if (!wordIdx[i] || !isRtlWord(tokens[i])) { i++; continue; }
        let j = i;
        while (j < tokens.length && wordIdx[j] && isRtlWord(tokens[j])) j++;
        const run = reordered.slice(i, j).map(rtlDisplayWord).reverse();
        for (let k = i; k < j; k++) reordered[k] = run[k - i];
        i = j;
    }
    return reordered.join('');
}

/**
 * Render-order transform for a whole text value: per-line reordering
 * (logical source text is never mutated).
 */
export function reorderRtlText(text: string): string {
    if (!text || !hasRtl(text)) return text;
    return text.split('\n').map(reorderRtlLine).join('\n');
}
